import { CurrencyPipe, DatePipe, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import {
  INSTRUCTOR_DRAWER_TAB,
  INSTRUCTOR_SETTINGS_DRAWER_TAB,
  PaginationConfig,
  RefundQueryParams,
  TabItem,
  UntilDestroy,
  untilDestroyed,
} from '@shared';
import {
  CombinationSingleInputItem,
  CombinationSingleInputValue,
} from '@shared/components/combination-single-input/combination-single-input.component';
import { PAYOUT_METHOD_IDS, PAYOUT_METHOD_NAMES } from '@shared/constants/payout';
import { BreadcrumbService } from '@shared/services/breadcrumb/breadcrumb.service';
import { PayoutService } from '@shared/services/payout/payout.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
  of,
  Subscription,
  switchMap,
} from 'rxjs';
import {
  AdminInactivePayoutsQuery,
  AdminPayoutRequest,
  InactivePayoutSummary,
  ModalComponent,
  Pagination,
  PaginationService,
  QueryFormConverter,
  QueryStringFilterService,
  RouterStoreService,
  ToastService,
} from 'thkee-common';
import { MessagesComponent } from '../../../../../../../thkee-common/src/lib/components/messages/messages.component';
import { ProcessingDirective } from '../../../../../../../thkee-common/src/lib/directives/processing.directive';
import { TooltipDirective } from '../../../../../../../thkee-common/src/lib/directives/tooltip.directive';
import { CombinationSingleInputComponent } from '../../../../@shared/components/combination-single-input/combination-single-input.component';
import { CommonPaginationComponent } from '../../../../@shared/components/common-pagination/common-pagination.component';
import { DrawerComponent } from '../../../../@shared/components/drawer/drawer.component';
import { StudentProfileComponent } from '../../../../@shared/components/student-profile/student-profile.component';
import { MarkPayoutAsPaidComponent } from '../../components/mark-payout-as-paid/mark-payout-as-paid.component';
const pageSize = 10;

@UntilDestroy()
@Component({
  selector: 'app-inactive-payout',
  templateUrl: './inactive-payout.component.html',
  styleUrls: ['./inactive-payout.component.scss'],
  providers: [QueryStringFilterService.forComponent()],
  imports: [
    AngularSvgIconModule,
    TooltipDirective,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    CombinationSingleInputComponent,
    RouterLink,
    NgTemplateOutlet,
    CommonPaginationComponent,
    ModalComponent,
    MessagesComponent,
    ProcessingDirective,
    DrawerComponent,
    StudentProfileComponent,
    MarkPayoutAsPaidComponent,
    CurrencyPipe,
    DatePipe,
  ],
})
export class InactivePayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  private toastService = inject(ToastService);
  private routerStore = inject(RouterStoreService);
  private breadcrumbService = inject(BreadcrumbService);
  private payoutService = inject(PayoutService);
  private queryStringFilterService =
    inject<QueryStringFilterService<AdminInactivePayoutsQuery>>(QueryStringFilterService);
  private paginationService = inject(PaginationService);
  private activatedRoute = inject(ActivatedRoute);

  searchValue: CombinationSingleInputValue | undefined;
  params: any = {};
  isLoading: Boolean = false;
  queryParams: RefundQueryParams = { page: 1, page_size: pageSize };

  //Student TAB
  instructorTabLists: TabItem[] = INSTRUCTOR_DRAWER_TAB;

  // subscription
  subscriptions = new Subscription();

  //Student Settings TAB
  instructorSettingsTabLists: TabItem[] = INSTRUCTOR_SETTINGS_DRAWER_TAB;

  // Filter form
  filterForm = new FormGroup({});
  filterFormModel: any = {};
  filterFormFields: FormlyFieldConfig[] = [
    {
      template: `<div class="text-base text-neutral-800 font-semibold border-b border-neutral-100 pb-2.5 mb-2.5">Account Status</div>`,
    },
    {
      key: 'account_status',
      fieldGroup: [
        {
          key: 'true',
          type: 'checkbox',
          className: '',
          props: {
            label: 'Active',
            required: true,
          },
        },
        {
          key: 'false',
          type: 'checkbox',
          className: '',
          props: {
            label: 'Inactive',
            required: true,
          },
        },
      ],
    },
    {
      template: `<div class="text-base text-neutral-800 font-semibold border-b border-neutral-100 pb-2.5 mb-2.5">Payout Feature Status</div>`,
    },
    {
      key: 'payout_feature_status',
      fieldGroup: [
        {
          key: 'true',
          type: 'checkbox',
          className: '',
          props: {
            label: 'Active',
            required: true,
          },
        },
        {
          key: 'false',
          type: 'checkbox',
          className: '',
          props: {
            label: 'Inactive',
            required: true,
          },
        },
      ],
    },
    {
      template: `<div class="text-base text-neutral-800 font-semibold border-b border-neutral-100 pb-2.5 mb-2.5">Payout Method</div>`,
    },
    {
      key: 'payout_method',
      fieldGroup: [
        {
          key: PAYOUT_METHOD_IDS.paypal,
          type: 'checkbox',
          className: '',
          props: {
            label: PAYOUT_METHOD_NAMES.paypal,
            required: true,
          },
        },
        {
          key: PAYOUT_METHOD_IDS.payoneer,
          type: 'checkbox',
          className: '',
          props: {
            label: PAYOUT_METHOD_NAMES.payoneer,
            required: true,
          },
        },
        {
          key: PAYOUT_METHOD_IDS.bank_account,
          type: 'checkbox',
          className: '',
          props: {
            label: PAYOUT_METHOD_NAMES.bank_account,
            required: true,
          },
        },
      ],
    },
    {
      template: `<div class="text-base text-neutral-800 font-semibold border-b border-neutral-100 pb-2.5 mt-2">Payout Amount</div>`,
    },
    {
      fieldGroupClassName: 'flex gap-2 items-center mb-4',
      fieldGroup: [
        {
          key: 'min',
          type: 'input',
          className: 'w-[78px]',
          props: {
            label: '',
            placeholder: '$ 1',
          },
        },
        {
          template: '-',
        },
        {
          key: 'max',
          type: 'input',
          className: 'w-[78px]',
          props: {
            label: '',
            placeholder: '$ 20',
          },
        },
      ],
    },
    {
      template: `<div class="text-base text-neutral-800 font-semibold border-b border-neutral-100 pb-2.5 mt-2">Payout Amount</div>`,
    },
    {
      fieldGroupClassName: 'flex gap-2 items-center mb-4',
      fieldGroup: [
        {
          key: 'requested_amount_min',
          type: 'input',
          className: 'w-[78px]',
          props: {
            label: '',
            placeholder: '1',
          },
        },
        {
          template: '-',
        },
        {
          key: 'requested_amount_max',
          type: 'input',
          className: 'w-[78px]',
          props: {
            label: '',
            placeholder: '20',
          },
        },
      ],
    },
    {
      template: `<div class="text-base text-neutral-800 font-semibold border-b border-neutral-100 pb-2.5 mt-2">Transactions</div>`,
    },
    {
      fieldGroupClassName: 'flex gap-2 items-center mb-4',
      fieldGroup: [
        {
          key: 'no_of_transactions_min',
          type: 'input',
          className: 'w-[78px]',
          props: {
            label: '',
            placeholder: '1',
          },
        },
        {
          template: '-',
        },
        {
          key: 'no_of_transactions_max',
          type: 'input',
          className: 'w-[78px]',
          props: {
            label: '',
            placeholder: '20',
          },
        },
      ],
    },
  ];
  query?: AdminInactivePayoutsQuery;

  // Active Payout Confirm
  activePayoutForm = new FormGroup({});
  activePayoutFormModel: any = {};
  activePayoutFormFields: FormlyFieldConfig[] = [
    {
      key: 'reason',
      type: 'textarea',
      props: {
        label: 'Description',
        labelClass: '!text-base',
        tips: 'Marke As Paid Confirm',
        placeholder: 'Enter a description...',
        minHeight: '110px',
        maxHeight: '150px',
      },
    },
  ];
  payoutsPagination?: Pagination<AdminPayoutRequest>;
  showingDropdown = '';
  searchOptions: CombinationSingleInputItem[] = [
    {
      id: 'id',
      title: 'ID',
      placeholder: 'Search by id',
    },
    {
      id: 'name',
      title: 'Instructor Name',
      placeholder: 'Search by instructor',
    },
  ];
  processingPayout?: AdminPayoutRequest;

  private queryFormConverter = new QueryFormConverter<AdminInactivePayoutsQuery>([
    'account_status',
    'payout_method',
    'payout_feature_status',
  ]);
  requesting: boolean = false;

  ngOnInit(): void {
    this.getSummary();
    this.initialUiData();
    this.queryUpdater();
  }

  private queryUpdater() {
    firstValueFrom(this.activatedRoute.queryParams).then((params) => {
      this.queryParams.page = params['page'] ? +params['page'] : 1;
      this.queryParams.page_size = params['page_size'] ? +params['page_size'] : 10;
      this.getInactivePayouts();
    });
  }

  ngAfterViewInit(): void {
    this.setupFilter();
  }

  handleMarkAsPaid(ok: boolean) {
    if (ok) {
      this.queryStringFilterService.refresh();
    }
  }

  activate(modal: ModalComponent) {
    this.requesting = true;
    this.payoutService
      .activatePayoutRequest(this.processingPayout!.id, this.activePayoutForm.value as any)
      .pipe(catchError(() => of(false)))
      .subscribe((ok) => {
        this.requesting = false;
        this.processingPayout = undefined;
        if (ok) {
          this.queryStringFilterService.refresh();
          modal.close();
          this.toastService.message({
            type: 'message',
            message: 'Activate payout successfully',
          });
        }
      });
  }

  openConfirmation(modal: ModalComponent, payout: AdminPayoutRequest) {
    this.processingPayout = payout;
    modal.open();
  }

  search($event: CombinationSingleInputValue) {
    const { key, value } = $event;
    if (key && value) {
      this.queryStringFilterService.patch({ search: [key, value].join(':') });
      // this.queryStringFilterService.remove([
      //   'no_of_transactions_min',
      //   'no_of_transactions_max',
      //   'requested_amount_min',
      //   'requested_amount_max',
      //   'payout_method',
      //   'payout_feature_status',
      //   'account_status',
      // ]);
      return;
    }

    this.queryStringFilterService.remove(['search']);
  }

  paginationConfig: PaginationConfig = {
    pageCountSummary: true,
    pageSize: true,
    firstAndLast: true,
  };

  pageSizeAction(value: number) {
    this.queryParams.page_size = value;
    this.queryParams.page = 1;
    this.queryStringFilterService.patch({ page_size: value, page: 1 });
  }

  paginationAction(page_number: number) {
    this.queryParams.page = page_number;
    this.queryStringFilterService.patch({ page: page_number });
  }

  resetFilter() {
    this.filterForm.reset();
    this.showingDropdown = '';
  }

  private initialUiData() {
    this.routerStore.getParams().subscribe((params) => {
      this.params = params;
      let breadcrumb: any = [
        {
          label: 'E-Commerce',
          url: '/ecommerce/dashboard',
        },
        {
          label: 'Payouts',
          url: '/ecommerce/payout/paid-payout/all',
        },
        {
          label: 'Inactive Payout',
          url: '',
        },
      ];
      this.breadcrumbService.setBreadcrumbs(breadcrumb);
    });
  }

  toggleDropdown(type: string) {
    if (type && type !== this.showingDropdown) {
      this.showingDropdown = type;
      return;
    }

    this.showingDropdown = '';
  }

  // Status methode
  getStatus(status?: boolean) {
    let style = { bg: '', text: '' };
    if (status) {
      style.bg = 'bg-teal-50';
      style.text = 'text-teal-600';
    } else {
      style.bg = 'bg-neutral-50';
      style.text = 'text-neutral-600';
    }

    return style;
  }

  // Payment history
  public paymentHistory!: InactivePayoutSummary;
  isLoadingInactivePayout: boolean = false;
  isLoadingInactivePayoutSummary: boolean = false;

  // Dummy data
  public dummyData: any = [
    {
      id: '123',
      period: '2024-02-20T10:30:31.398Z',
      instructor: {
        id: 'b001',
        title: 'Faris Friansyah',
      },
      account_status: 'Active',
      payout_methode: 'Paypal',
      inactive_reason: 'Methode not set up',
      payout_amount: 344,
      transaction: 20,
      boldby: 'Frank',
    },
    {
      id: '123',
      period: '2024-02-20T10:30:31.398Z',
      instructor: {
        id: 'b001',
        title: 'Faris Friansyah',
      },
      account_status: 'Active',
      payout_methode: 'Paypal',
      inactive_reason: 'Methode not set up',
      payout_amount: 344,
      transaction: 20,
      boldby: 'Ibrahim',
    },
    {
      id: '123',
      period: '2024-02-20T10:30:31.398Z',
      instructor: {
        id: 'b001',
        title: 'Faris Friansyah',
      },
      account_status: 'Inactive',
      payout_methode: 'Paypal',
      inactive_reason: 'Methode not set up',
      payout_amount: 344,
      transaction: 20,
      boldby: 'Emran',
    },
  ];

  private getSummary() {
    this.isLoadingInactivePayoutSummary = true;
    this.payoutService.getInactivePayoutSummary().subscribe({
      next: (res) => {
        this.isLoadingInactivePayoutSummary = false;
        this.paymentHistory = res;
      },
      error: ({ error }) => {
        error.errors.map((error: { detail: string }) =>
          this.toastService.error({
            message: error.detail,
          })
        );
      },
    });
  }

  private getInactivePayouts() {
    this.isLoading = true;
    combineLatest([this.queryStringFilterService.valueChanges])
      .pipe(
        debounceTime(400),
        map(([query]) => query),
        distinctUntilChanged(),
        switchMap((query) => {
          return this.payoutService.getInactivePayouts(query);
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (res) => {
          this.payoutsPagination = res;
          this.paginationService.getPager(res.count, this.queryParams.page, this.queryParams.page_size);
          this.isLoadingInactivePayout = false;
          this.isLoading = false;
        },
        error: (error) => {
          this.payoutsPagination = undefined;
          this.isLoadingInactivePayout = false;
          this.isLoading = false;
        },
      });
  }

  private toSearchValue(query: AdminInactivePayoutsQuery) {
    const fields: (keyof AdminInactivePayoutsQuery)[] = ['account_status', 'payout_feature_status'];
    const fomattedValue = fields
      .filter((f) => query[f])
      .reduce((r, f) => {
        const value: string[] = [].concat(query[f] as any);
        if (value.length !== 1) {
          return { ...r, [f]: undefined };
        }
        return { ...r, [f]: value.includes('active') };
      }, {});
    return { ...query, ...fomattedValue };
  }

  private setupFilter() {
    this.filterForm.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.queryStringFilterService.patch(this.queryFormConverter.toQuery(value as any));
    });

    this.queryStringFilterService.initialValue.subscribe((query) => {
      this.query = query;
      if (query.search) {
        const [key, value] = query.search.split(':');
        if (key && value) {
          this.searchValue = { key, value };
        }
      }
      this.filterForm.patchValue(this.queryFormConverter.toForm(query));
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
